import React from "react";
import { Box, Breadcrumbs, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

function toLabel(segment) {
  return segment
    .replace(/-/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

function autoCrumbs(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (!parts.length) return [];
  const last = parts[parts.length - 1];
  return [{ label: toLabel(decodeURIComponent(last)), to: pathname }];
}

export default function PageHeader({ title, subtitle, breadcrumbs, actions, icon, children }) {
  const location = useLocation();
  const auto = autoCrumbs(location.pathname);
  const currentLabel = title || breadcrumbs?.[breadcrumbs.length - 1]?.label || auto[0]?.label;

  return (
    <Box sx={{ mb: 3 }}>
      {currentLabel && (
        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 14 }} />}
          // Breadcrumbs duplicate the page title and eat a whole row on phones —
          // hide them on xs and keep them for tablet/desktop orientation.
          sx={{ mb: 1.5, display: { xs: "none", sm: "flex" }, "& .MuiBreadcrumbs-ol": { alignItems: "center" } }}
        >
          <Link
            component={RouterLink}
            to="/EmployeeDashboard"
            underline="hover"
            sx={{ fontSize: 12, color: "text.secondary", fontWeight: 500 }}
          >
            Dashboard
          </Link>
          <Typography sx={{ fontSize: 12, color: "primary.main", fontWeight: 600 }}>
            {currentLabel}
          </Typography>
        </Breadcrumbs>
      )}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
          {icon && (
            <Box
              sx={{
                width: { xs: 38, sm: 44 },
                height: { xs: 38, sm: 44 },
                flexShrink: 0,
                borderRadius: 2.5,
                background: "linear-gradient(135deg, #EDE8FA 0%, #F4EDFA 100%)",
                color: "#6C50BF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: { xs: 19, sm: 22 } } })}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            {/* Render as <h1> for screen readers (each page should have
                exactly one top-level heading) while keeping the h4-sized
                visual treatment. Fixes axe-core page-has-heading-one and
                heading-order violations. */}
            <Typography component="h1" variant="h4" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" }, fontWeight: 700, lineHeight: 1.2, color: "text.primary" }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, fontSize: { xs: 12.5, sm: 13 } }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {actions && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {actions}
          </Stack>
        )}
      </Stack>
      {children}
    </Box>
  );
}
